/** @format */

import { apiSlice } from "../apiSlice";

export type ResourceResponse = {
  user: string;
  userResourceRefs: any[];
};

interface hwbMeta {
  name: string;
  sub: string;
  given_name: string;
  family_name: string;
  picture: string;
  email: string;
}

interface userMeta {
  name: string;
  lastLogin: string;
  avatar: string;
  uuid: string;
  hwb?: hwbMeta;
}

export type UserRole =
  | ""
  | "public"
  | "unknown"
  | "teacher"
  | "wjec"
  | "admin";

interface userResponse {
  role: UserRole;
  meta: userMeta;
  wjec: boolean;
  created: string;
  SK: string;
  PK: string;
  email: string;
  modified: string;
  emailHash: string;
}

export interface User {
  PK: string;
  SK: string;
  role: UserRole;
  wjec: boolean;
  email: string;
  emailHash: string;
  meta: userMeta;
  termsConditions?: string;
}

/** API Endpoints related to User(s) */
export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * updateUser mutation (POST)
     * @param terms the new terms value
     * @returns the updated terms value.
     */
    updateUserTerms: build.mutation<{ message: string }, { terms: string }>({
      query: (data) => {
        return {
          url: `user/updateTerms`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["User"],
    }),

    /**
     * getUserMeta query (GET)
     * @description gets the current user's metadata
     * @returns {userMeta & {emailHash: string}} the fetched user.
     */
    getUserMeta: build.query<userMeta & { emailHash: string }, void>({
      query: () => `user/get`,
      transformResponse(res: userResponse, meta, arg) {
        return { ...res.meta, emailHash: res.emailHash };
      },
      providesTags: ["User"],
    }),

    /**
     * getUserById query (GET)
     * @description gets the user from specified `id`
     * @param {string} userId - the user's id.
     * @returns {userResponse} the fetched user.
     */
    getUserById: build.query<userResponse, { userId: string }>({
      query: ({ userId }) => `user/get/${userId}`,
      providesTags: ["User"],
    }),

    /**
     * getUser query (GET)
     * @description gets the current user
     * @returns {User} the fetched user.
     */
    getUser: build.query<User, void>({
      query: () => `user/get`,
      providesTags: ["User"],
    }),

    /**
     * deleteUser mutation (DELETE)
     * @description Deletes the current user's account and all associated data
     * @returns confirmation that user account has been deleted
     */
    deleteUser: build.mutation<string, void>({
      query: () => ({
        url: "user/delete-user",
        method: "DELETE",
      }),
      transformResponse: (response: { message: string }) => response.message,
      invalidatesTags: ["User", "UserResourceRef", "UserResources"],
    }),
  }),
});

export const {
  useGetUserByIdQuery,
  useGetUserMetaQuery,
  useGetUserQuery,
  useUpdateUserTermsMutation,
  useDeleteUserMutation,
} = userApiSlice;
